import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { PerimetreUtilisateurRow, SousSegmentRow } from '@/lib/database.types';

export function useSousSegments(activiteId: string | null) {
  return useQuery({
    queryKey: ['sous_segments', activiteId],
    enabled: !!activiteId,
    queryFn: async (): Promise<SousSegmentRow[]> => {
      if (!activiteId) return [];
      const { data, error } = await supabase
        .from('sous_segments')
        .select('*')
        .eq('activite_id', activiteId)
        .order('ordre');
      if (error) throw error;
      return data as unknown as SousSegmentRow[];
    },
  });
}

export function useAllSousSegments() {
  return useQuery({
    queryKey: ['sous_segments_all'],
    queryFn: async (): Promise<SousSegmentRow[]> => {
      const { data, error } = await supabase.from('sous_segments').select('*').order('ordre');
      if (error) throw error;
      return data as unknown as SousSegmentRow[];
    },
  });
}

export function usePerimetresUser(userId: string | null) {
  return useQuery({
    queryKey: ['perimetres_user', userId],
    enabled: !!userId,
    queryFn: async (): Promise<PerimetreUtilisateurRow[]> => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from('perimetres_utilisateurs')
        .select('*')
        .eq('user_id', userId)
        .order('created_at');
      if (error) throw error;
      return data as unknown as PerimetreUtilisateurRow[];
    },
  });
}

export function useAllPerimetres() {
  return useQuery({
    queryKey: ['perimetres_all'],
    queryFn: async (): Promise<PerimetreUtilisateurRow[]> => {
      const { data, error } = await supabase
        .from('perimetres_utilisateurs')
        .select('*')
        .limit(2000);
      if (error) throw error;
      return data as unknown as PerimetreUtilisateurRow[];
    },
  });
}

export function useReplacePerimetres() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userId: string;
      perimetres: {
        entreprise_id: string;
        activite_id: string;
        sous_segment_id: string | null;
        pipeline_ids?: string[] | null;
        peut_voir_consolide?: boolean;
        peut_modifier?: boolean;
        peut_assigner_a_autres?: boolean;
        date_debut?: string;
        date_fin?: string | null;
      }[];
    }): Promise<void> => {
      // Suppression puis ré-insertion (transaction côté front, pas atomique mais OK pour une UI admin)
      const { error: delErr } = await supabase
        .from('perimetres_utilisateurs')
        .delete()
        .eq('user_id', input.userId);
      if (delErr) throw delErr;
      if (input.perimetres.length === 0) return;
      const records = input.perimetres.map((p) => ({ ...p, user_id: input.userId }));
      const { error } = await supabase.from('perimetres_utilisateurs').insert(records);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['perimetres_user', vars.userId] });
      void qc.invalidateQueries({ queryKey: ['perimetres_all'] });
    },
  });
}
